import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BellRing, Monitor, Moon, ShieldCheck, Sun, X } from 'lucide-react';
import { useStore, isPremium, type ThemePref, type TextScale } from '../store/useStore';
import { binDaysLeft, visibleBin } from '../engine/bin';
import { APP_VERSION, PLANS } from '../types';
import { isIOS, isNative, publicUrl } from '../native/native';
import { planTitleWithPrice } from '../billing/prices';
import { STORE_BILLING_LIVE } from '../billing/launch';
import { productIdForPlan } from '../billing/revenueCatBilling';
import { billingFooter, manageSubscriptionsUrl, storeName } from '../billing/storeLabels';
import { canLinkApple } from '../sync/syncCore';
import { EmailPasswordSignIn } from './EmailPasswordSignIn';
import { DeleteAccountPanel } from './DeleteAccountPanel';

/** Simple labeled on/off row used by accessibility & reminder settings. */
function ToggleRow({
  label,
  hint,
  on,
  disabled = false,
  onChange,
}: {
  label: string;
  hint: string;
  on: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      onClick={() => !disabled && onChange(!on)}
      aria-pressed={on}
      disabled={disabled}
      className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50 p-3.5 text-left disabled:opacity-50"
    >
      <span>
        <span className="block text-xs font-bold text-slate-900 dark:text-white">{label}</span>
        <span className="mt-0.5 block text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
          {hint}
        </span>
      </span>
      <span
        className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
          on ? 'bg-brand-600' : 'bg-slate-300 dark:bg-slate-700'
        }`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
            on ? 'left-[22px]' : 'left-0.5'
          }`}
        />
      </span>
    </button>
  );
}

/** Settings bottom sheet: subscription, appearance, sync, data, help. */
export function SettingsSheet() {
  const open = useStore((s) => s.settingsOpen);
  const closeSettings = useStore((s) => s.closeSettings);
  const subscription = useStore((s) => s.subscription);
  const cancelSubscription = useStore((s) => s.cancelSubscription);
  const restorePurchases = useStore((s) => s.restorePurchases);
  const billingNotice = useStore((s) => s.billingNotice);
  const billingBusy = useStore((s) => s.billingBusy);
  const billingTrace = useStore((s) => s.billingTrace);
  const billingSelfTest = useStore((s) => s.billingSelfTest);
  const scansRemaining = useStore((s) => s.scansRemaining);
  const freePlansRemaining = useStore((s) => s.freePlansRemaining);
  const bin = useStore((s) => s.bin);
  const restoreFromBin = useStore((s) => s.restoreFromBin);
  const emptyBin = useStore((s) => s.emptyBin);
  const purgeFromBin = useStore((s) => s.purgeFromBin);
  /** Two-tap confirm, same as the board Bin — see MatrixScreen. */
  const [purgeArmedId, setPurgeArmedId] = useState<string | null>(null);
  const openPaywall = useStore((s) => s.openPaywall);

  const syncStatus = useStore((s) => s.syncStatus);
  const syncError = useStore((s) => s.syncError);
  const syncNotice = useStore((s) => s.syncNotice);
  const syncEmail = useStore((s) => s.syncEmail);
  const appleNewAccount = useStore((s) => s.appleNewAccount);
  const syncProviders = useStore((s) => s.syncProviders);
  const lastAccount = useStore((s) => s.lastAccount);
  const aiConsent = useStore((s) => s.aiConsent);
  const setAiProcessing = useStore((s) => s.setAiProcessing);

  const premium = isPremium(subscription);
  const planMeta = PLANS.find((p) => p.id === subscription.plan);
  /**
   * Only a RECURRING plan can be cancelled. A lifetime purchase has nothing
   * to cancel, and an alpha grant must never be cancellable — the flag is
   * checked separately so a provider that has clobbered `plan` can't put
   * the button back on screen.
   */
  const cancellable =
    !subscription.alphaTester && subscription.plan !== 'lifetime';

  const theme = useStore((s) => s.theme);
  const setTheme = useStore((s) => s.setTheme);
  const textScale = useStore((s) => s.textScale);
  const setTextScale = useStore((s) => s.setTextScale);
  const calmMotion = useStore((s) => s.calmMotion);
  const setCalmMotion = useStore((s) => s.setCalmMotion);
  const dailyAffirmationPref = useStore((s) => s.dailyAffirmation);
  const setDailyAffirmationPref = useStore((s) => s.setDailyAffirmationPref);
  const easyFont = useStore((s) => s.easyFont);
  const setEasyFont = useStore((s) => s.setEasyFont);
  const weeklyReminder = useStore((s) => s.weeklyReminder);
  const setWeeklyReminderPref = useStore((s) => s.setWeeklyReminderPref);
  const dailyNudge = useStore((s) => s.dailyNudge);
  const setDailyNudgePref = useStore((s) => s.setDailyNudgePref);
  const native = isNative();
  const storePrices = useStore((s) => s.storePrices);
  const signIn = () => void import('../sync/firebaseSync').then((m) => m.signInWithGoogle());
  const signInApple = () => void import('../sync/firebaseSync').then((m) => m.signInWithApple());
  const signOut = () => void import('../sync/firebaseSync').then((m) => m.signOutSync());
  const chooseGoogleInstead = () =>
    void import('../sync/firebaseSync').then((m) => m.abandonNewAppleAccount());
  const keepAppleAccount = () =>
    void import('../sync/firebaseSync').then((m) => m.keepNewAppleAccount());
  const linkApple = () =>
    void import('../sync/firebaseSync').then((m) => m.linkAppleToCurrentAccount());

  const [linkEmail, setLinkEmail] = useState('');
  const [linkState, setLinkState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  // Resend cooldown: a sent link must NEVER be a dead end (a tester was
  // stranded when the email didn't arrive and there was no way forward).
  const [resendIn, setResendIn] = useState(0);
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);
  const [cloudDelete, setCloudDelete] = useState<'idle' | 'working' | 'deleted' | 'error'>('idle');

  /** GDPR-style portability: download everything as readable JSON. */
  const exportData = () => {
    const s = useStore.getState();
    const payload = {
      app: 'ZoneIn AI',
      exportVersion: 1,
      exportedAt: new Date().toISOString(),
      tasks: Object.values(s.tasks),
      archive: s.archive,
      weekHistory: s.weekHistory,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `zonein-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const deleteCloud = async () => {
    if (
      !window.confirm(
        'Delete your synced data from the cloud? Your tasks stay on this device, and sync turns off. You can sign in again anytime to re-enable it.',
      )
    ) {
      return;
    }
    setCloudDelete('working');
    const { deleteCloudData } = await import('../sync/firebaseSync');
    const result = await deleteCloudData();
    setCloudDelete(result === 'deleted' ? 'deleted' : 'error');
  };
  const sendLink = async () => {
    if (!/.+@.+\..+/.test(linkEmail)) return;
    setLinkState('sending');
    const { sendEmailLink } = await import('../sync/firebaseSync');
    const ok = (await sendEmailLink(linkEmail.trim())) === 'sent';
    setLinkState(ok ? 'sent' : 'error');
    if (ok) setResendIn(30);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 dark:bg-slate-950/70 backdrop-blur-sm"
          onClick={closeSettings}
        >
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] w-full max-w-md space-y-5 overflow-y-auto rounded-t-3xl border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-2xl"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-extrabold text-slate-900 dark:text-white">Settings</h3>
              <button
                onClick={closeSettings}
                aria-label="Close settings"
                className="rounded-full p-1.5 text-slate-500 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800"
              >
                <X size={18} />
              </button>
            </div>

            {/* Subscription */}
            <section className="space-y-2">
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500">
                Subscription
              </h4>
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50 p-4">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {subscription.alphaTester && 'Alpha Tester — Lifetime access (thank you!) '}
                  {!subscription.alphaTester && subscription.plan === 'trial' && premium && 'Free Trial (active)'}
                  {!subscription.alphaTester && planMeta && planTitleWithPrice(planMeta, storePrices[planMeta.id], native)}
                  {!premium && subscription.plan === 'free' && 'Free plan'}
                </p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {premium
                    ? 'Unlimited AI drill-downs and scans are unlocked.'
                    : `Magic Sort is free forever. Free AI action plans left: ${freePlansRemaining}. Scans left: ${scansRemaining}.`}
                </p>
                <div className="mt-3">
                  {/* Lifetime access has nothing to cancel — and offering the
                      button to an alpha-grant holder invited them to destroy
                      the access they were given (found live, 24 Aug 2026).
                      The grant itself is now guarded in code regardless
                      (billing/grantGuard.ts), but the button should never
                      have been on screen for them in the first place. */}
                  {premium && !cancellable ? (
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                      Nothing to cancel — your access doesn&rsquo;t expire.
                    </p>
                  ) : premium && STORE_BILLING_LIVE ? (
                    /* Stores do not let an app cancel a subscription. The
                       honest button is a link into the store's own
                       subscription centre (storeLabels: App Store on iOS,
                       Play elsewhere) — same target="_blank" path the
                       waitlist links already use (§5: proven path). */
                    <a
                      href={manageSubscriptionsUrl(productIdForPlan(subscription.plan))}
                      target="_blank"
                      rel="noopener"
                      className="inline-block rounded-lg border border-slate-300 dark:border-slate-700 px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800"
                    >
                      Manage or cancel in {storeName()}
                    </a>
                  ) : premium ? (
                    <button
                      onClick={() => void cancelSubscription()}
                      className="rounded-lg border border-slate-300 dark:border-slate-700 px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800"
                    >
                      Cancel in one click
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        closeSettings();
                        openPaywall();
                      }}
                      className="rounded-lg bg-brand-600 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-brand-500"
                    >
                      Go Premium
                    </button>
                  )}
                </div>
                {/* Restore purchases — new phone, reinstall, or a purchase
                    made before signing in. Store-only; the mock has nothing
                    to restore. Alpha-grant holders never need it (their
                    grant restores from the cloud), so it hides for them. */}
                {STORE_BILLING_LIVE && isNative() && !subscription.alphaTester && (
                  <div className="mt-3 border-t border-slate-200 dark:border-slate-800 pt-3">
                    <button
                      onClick={() => void restorePurchases()}
                      disabled={billingBusy}
                      className="text-xs font-semibold text-brand-600 dark:text-brand-400 hover:underline disabled:opacity-60"
                    >
                      {billingBusy ? `Checking ${storeName()}…` : 'Restore purchases'}
                    </button>
                    <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                      Bought premium on another phone? Sign in with the same account, then tap here.
                    </p>
                  </div>
                )}
                {billingNotice && (
                  <p
                    role="status"
                    className="mt-3 break-words rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-[11px] leading-relaxed text-amber-800 dark:text-amber-200"
                  >
                    {billingNotice}
                  </p>
                )}
                {/* Billing diagnostics (CLAUDE.md §2). Every store step narrates
                    itself here; a line that says "started" with no follow-up is
                    the diagnosis. The self-test configures and lists offerings
                    without buying anything. Native + live builds only. */}
                {/* 12 Oct 2026: folded under "Troubleshooting" — a visible
                    "self-test" reads as unfinished to customers and App Review.
                    The trace is still one tap away, and store errors still
                    surface verbatim in billingNotice above (§2). */}
                {STORE_BILLING_LIVE && isNative() && (
                  <details className="mt-3 border-t border-slate-200 dark:border-slate-800 pt-3">
                    <summary className="cursor-pointer text-[11px] font-semibold text-slate-500 dark:text-slate-400">
                      Troubleshooting
                    </summary>
                    <button
                      onClick={() => void billingSelfTest()}
                      disabled={billingBusy}
                      className="text-xs font-semibold text-slate-600 dark:text-slate-300 hover:underline disabled:opacity-60"
                    >
                      {billingBusy ? `Checking ${storeName()}…` : `Check connection to ${storeName()}`}
                    </button>
                    {billingTrace.length > 0 && (
                      <pre
                        aria-label="Billing diagnostics"
                        className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-slate-100 dark:bg-slate-900 px-2 py-1.5 font-mono text-[10px] leading-snug text-slate-600 dark:text-slate-300"
                      >
                        {billingTrace.join('\n')}
                      </pre>
                    )}
                  </details>
                )}
              </div>
            </section>

            {/* Appearance */}
            <section className="space-y-2">
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500">
                Appearance
              </h4>
              <div className="grid grid-cols-3 gap-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50 p-2">
                {(
                  [
                    { id: 'light', label: 'Light', icon: Sun },
                    { id: 'dark', label: 'Dark', icon: Moon },
                    { id: 'system', label: 'System', icon: Monitor },
                  ] as { id: ThemePref; label: string; icon: typeof Sun }[]
                ).map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setTheme(id)}
                    aria-pressed={theme === id}
                    className={`flex flex-col items-center gap-1 rounded-lg py-2.5 text-[11px] font-bold transition-colors ${
                      theme === id
                        ? 'bg-brand-600 text-white shadow'
                        : 'text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800'
                    }`}
                  >
                    <Icon size={16} />
                    {label}
                  </button>
                ))}
              </div>
              <p className="px-1 text-[11px] leading-relaxed text-slate-500">
                System follows your device setting. Both themes meet contrast
                guidelines; pick whichever is easier on your eyes.
              </p>
            </section>

            {/* Accessibility */}
            <section className="space-y-2">
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500">
                Accessibility
              </h4>
              <div className="grid grid-cols-3 gap-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50 p-2">
                {(
                  [
                    { id: 'compact', label: 'Compact', sample: 'Aa' },
                    { id: 'default', label: 'Default', sample: 'Aa' },
                    { id: 'relaxed', label: 'Relaxed', sample: 'Aa' },
                  ] as { id: TextScale; label: string; sample: string }[]
                ).map(({ id, label, sample }) => (
                  <button
                    key={id}
                    onClick={() => setTextScale(id)}
                    aria-pressed={textScale === id}
                    className={`flex flex-col items-center gap-0.5 rounded-lg py-2 text-[11px] font-bold transition-colors ${
                      textScale === id
                        ? 'bg-brand-600 text-white shadow'
                        : 'text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800'
                    }`}
                  >
                    <span
                      className={
                        id === 'compact' ? 'text-xs' : id === 'relaxed' ? 'text-lg' : 'text-sm'
                      }
                    >
                      {sample}
                    </span>
                    {label}
                  </button>
                ))}
              </div>
              <ToggleRow
                label="Calmer motion"
                hint="Stops pulsing and looping animations that pull attention."
                on={calmMotion}
                onChange={setCalmMotion}
              />
              <ToggleRow
                label="Easier-reading font"
                hint="Atkinson Hyperlegible — clearer letterforms, easier on dyslexic readers."
                on={easyFont}
                onChange={setEasyFont}
              />
              <ToggleRow
                label="Daily affirmation"
                hint="A morning note grounded in your own numbers — honest on rough weeks, never generic. Generated on this device; nothing is sent anywhere."
                on={dailyAffirmationPref}
                onChange={setDailyAffirmationPref}
              />
            </section>

            {/* Reminders */}
            <section className="space-y-2">
              <h4 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
                <BellRing size={13} />
                Reminders
              </h4>
              <ToggleRow
                label="Weekly wrap-up reminder"
                hint="One gentle note on Sunday evening — never an alarm."
                on={weeklyReminder}
                disabled={!native}
                onChange={setWeeklyReminderPref}
              />
              <ToggleRow
                label="Daily “one thing” nudge"
                hint="A morning invitation to pick a single task. Off by default."
                on={dailyNudge}
                disabled={!native}
                onChange={setDailyNudgePref}
              />
              {!native && (
                <p className="px-1 text-[11px] leading-relaxed text-slate-500">
                  Reminders arrive with the iOS / Android app — they need the
                  installed app, not the browser.
                </p>
              )}
            </section>

            {/* Cloud sync */}
            <section className="space-y-2">
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500">
                Cloud Sync
              </h4>
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50 p-4">
                {syncNotice && (
                  <p className="mb-3 break-words rounded-lg border border-brand-100 dark:border-brand-500/20 bg-brand-50 dark:bg-brand-900/20 p-2.5 text-[11px] leading-relaxed text-brand-700 dark:text-brand-100">
                    {syncNotice}
                  </p>
                )}
                {/* Apple/Google account guard (10 Oct 2026): an Apple sign-in
                    that would CREATE a new account is held here until the
                    user chooses — see syncCore.appleSignInDecision. */}
                {appleNewAccount && (
                  <div className="mb-3 rounded-lg border border-amber-200 dark:border-amber-500/20 bg-amber-50 dark:bg-amber-950/20 p-3 text-xs leading-relaxed text-amber-800 dark:text-amber-200">
                    <p className="font-bold">This Apple ID isn’t connected to a ZoneIn account yet.</p>
                    <p className="mt-1">
                      Apple shared {appleNewAccount.email ? <b>{appleNewAccount.email}</b> : 'a hidden email address'}.
                      Already use ZoneIn on Android or the web? Use the same sign-in you use
                      there, so your tasks and any lifetime access come with you.
                    </p>
                    <div className="mt-2.5 flex flex-wrap gap-2">
                      <button
                        onClick={chooseGoogleInstead}
                        className="rounded-lg bg-brand-600 px-3 py-2 text-[11px] font-bold text-white transition-colors hover:bg-brand-500"
                      >
                        Use Google instead
                      </button>
                      <button
                        onClick={keepAppleAccount}
                        className="rounded-lg border border-amber-300 dark:border-amber-500/30 px-3 py-2 text-[11px] font-bold text-amber-800 dark:text-amber-200 transition-colors hover:bg-amber-100 dark:hover:bg-amber-900/30"
                      >
                        I’m new — start a new account
                      </button>
                    </div>
                  </div>
                )}
                {syncStatus === 'disabled' && (
                  <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                    Sync is off — this device runs local-only. To enable
                    real-time sync across all your devices, add your Firebase
                    project config to <code className="text-brand-600 dark:text-brand-400">.env</code>{' '}
                    (<code className="text-brand-600 dark:text-brand-400">VITE_FIREBASE_CONFIG</code>)
                    and rebuild. Setup steps are in{' '}
                    <span className="font-semibold">docs/ARCHITECTURE.md §6</span>.
                  </p>
                )}
                {syncStatus === 'signed-out' && (
                  <>
                    <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                      Sign in once and your matrix, weekly history, and
                      analytics stay in sync on every device — phone, tablet,
                      and desktop — in real time. No account needed to use the
                      app; sign-in is only for sync.
                    </p>
                    <button
                      onClick={signIn}
                      className="mt-3 rounded-lg bg-brand-600 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-brand-500"
                    >
                      Sign in with Google
                    </button>
                    {/* iOS: Sign in with Apple beside Google (App Review 4.8). */}
                    {isIOS() && (
                      <button
                        onClick={signInApple}
                        className="ml-2 mt-3 rounded-lg bg-slate-900 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-slate-700 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
                      >
                         Sign in with Apple
                      </button>
                    )}
                    {/* Lifetime holders on iPhone: the code field is hidden on
                        iOS (App Review 3.1.1), so the account IS the way in.
                        Same sentence as the paywall (7 Oct 2026). */}
                    {isIOS() && (
                      <p className="mt-3 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                        Already have lifetime access? Sign in with the same account you used before — your access follows your account.
                      </p>
                    )}
                    {/* Email + password (13 Oct 2026): every platform. The
                        form shows its own outcome under its button. */}
                    <div className="mt-3 border-t border-slate-200 dark:border-slate-800 pt-3">
                      <EmailPasswordSignIn startOpen={lastAccount?.provider === 'password'} />
                    </div>
                    {/* Email-link sign-in can't complete inside the native
                        shell (the link opens in the phone's browser, a
                        different app context) — native has email + password
                        above instead; web offers both. */}
                    {!isNative() && (
                    <div className="mt-3 border-t border-slate-200 dark:border-slate-800 pt-3">
                      <p className="mb-2 text-[11px] font-semibold text-slate-500 dark:text-slate-400">
                        No Google account? Use any email — we'll send a one-tap
                        sign-in link. No password needed.
                      </p>
                      {linkState === 'sent' ? (
                        <div className="space-y-2">
                          <p className="rounded-lg border border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-950/20 p-2.5 text-[11px] text-emerald-700 dark:text-emerald-400">
                            ✓ Link sent to {linkEmail}. Open it on this device
                            to finish signing in.
                          </p>
                          <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                            Nothing after a minute? Check spam — some email
                            providers delay or block sign-in links.
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => void sendLink()}
                              disabled={resendIn > 0 || linkState !== 'sent'}
                              className="rounded-lg border border-slate-300 dark:border-slate-700 px-3 py-2 text-[11px] font-bold text-slate-700 dark:text-slate-300 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
                            >
                              {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend link'}
                            </button>
                            <button
                              onClick={() => {
                                setLinkState('idle');
                                setResendIn(0);
                              }}
                              className="rounded-lg px-3 py-2 text-[11px] font-semibold text-slate-500 dark:text-slate-400 transition-colors hover:text-slate-700 dark:hover:text-slate-200"
                            >
                              Use a different email
                            </button>
                          </div>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400">
                            Or skip email entirely —{' '}
                            <button
                              onClick={signIn}
                              className="font-bold text-brand-600 dark:text-brand-400 underline-offset-2 hover:underline"
                            >
                              sign in with Google
                            </button>{' '}
                            works instantly.
                          </p>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <input
                            type="email"
                            value={linkEmail}
                            onChange={(e) => setLinkEmail(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && void sendLink()}
                            placeholder="you@anywhere.com"
                            autoComplete="email"
                            className="min-w-0 flex-1 rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2 text-xs text-slate-900 dark:text-white placeholder-slate-400 focus:border-brand-500 focus:outline-none"
                          />
                          <button
                            onClick={() => void sendLink()}
                            disabled={linkState === 'sending'}
                            className="whitespace-nowrap rounded-lg border border-slate-300 dark:border-slate-700 px-3 py-2 text-[11px] font-bold text-slate-700 dark:text-slate-300 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-60"
                          >
                            {linkState === 'sending' ? 'Sending…' : 'Email me a link'}
                          </button>
                        </div>
                      )}
                      {linkState === 'error' && (
                        <p className="mt-1.5 text-[11px] text-red-600 dark:text-red-400">
                          Couldn't send the link — check the address and that
                          email sign-in is enabled in Firebase.
                        </p>
                      )}
                    </div>
                    )}
                  </>
                )}
                {syncStatus === 'syncing' && (
                  <>
                    <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                      ✓ Sync active
                    </p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Signed in as {syncEmail}. Changes sync to your other
                      devices within seconds. Subscriptions and API keys never
                      leave this device.
                    </p>
                    {/* One account, both buttons (10 Oct 2026): lets a Google
                        account also open with Apple on iPhone. */}
                    {canLinkApple(syncProviders, isIOS()) && (
                      <div className="mt-3 border-t border-slate-200 dark:border-slate-800 pt-3">
                        <button
                          onClick={linkApple}
                          className="rounded-lg bg-slate-900 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-slate-700 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
                        >
                           Also allow Sign in with Apple
                        </button>
                        <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                          Optional. Afterwards either button opens this same account.
                        </p>
                      </div>
                    )}
                    {syncError && (
                      <p className="mt-2 break-words rounded-lg bg-red-50 dark:bg-red-950/20 p-2 font-mono text-[10px] leading-relaxed text-red-700 dark:text-red-300">
                        {syncError}
                      </p>
                    )}
                    <button
                      onClick={signOut}
                      className="mt-3 rounded-lg border border-slate-300 dark:border-slate-700 px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800"
                    >
                      Sign out
                    </button>
                  </>
                )}
                {syncStatus === 'error' && (
                  <>
                    <p className="text-xs text-red-600 dark:text-red-400">
                      Sign-in or sync hit an error.
                    </p>
                    {syncError && (
                      <p className="mt-1 break-words rounded-lg bg-red-50 dark:bg-red-950/20 p-2 font-mono text-[10px] leading-relaxed text-red-700 dark:text-red-300">
                        {syncError}
                      </p>
                    )}
                    <button
                      onClick={signIn}
                      className="mt-3 rounded-lg border border-slate-300 dark:border-slate-700 px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800"
                    >
                      Retry sign-in
                    </button>
                    <div className="mt-3 border-t border-slate-200 dark:border-slate-800 pt-3">
                      <EmailPasswordSignIn startOpen={lastAccount?.provider === 'password'} />
                    </div>
                  </>
                )}
              </div>
            </section>

            {/* Privacy & Data */}
            <section className="space-y-2">
              <h4 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
                <ShieldCheck size={13} />
                Privacy & Data
              </h4>
              <div className="space-y-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50 p-4">
                <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                  <span className="font-bold text-emerald-600 dark:text-emerald-400">✓ No trackers.</span>{' '}
                  No analytics, no ads, no cross-app tracking, no data sale.
                </p>
                <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                  <span className="font-bold text-emerald-600 dark:text-emerald-400">✓ Local-first.</span>{' '}
                  Your tasks live on this device. Cloud sync is opt-in, and your
                  cloud record is readable by your sign-in only.
                </p>
                <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                  <span className="font-bold text-emerald-600 dark:text-emerald-400">✓ AI that doesn't train on you.</span>{' '}
                  Sorting runs on Google Cloud Vertex AI through our private
                  server — your content is not used to train AI models, and no
                  AI credentials exist inside this app.
                </p>
                {/* AI processing consent, revocable here (17 Oct 2026, App
                    Review 5.1.1(ii)/5.1.2(i)). Off = the store refuses every
                    AI action AND the network layer refuses to send; the next
                    AI tap shows the consent sheet again. */}
                <label className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2.5">
                  <span className="text-xs leading-relaxed text-slate-700 dark:text-slate-300">
                    <span className="font-bold text-slate-900 dark:text-white">AI processing</span>
                    <br />
                    {aiConsent === 'granted'
                      ? 'On — task text and list photos are sent to our Google Cloud server only when you tap an AI action.'
                      : aiConsent === 'declined'
                        ? 'Off — nothing is sent. AI sorting, plans and scans ask for your agreement first.'
                        : 'Not decided yet — the first AI action will ask for your agreement.'}
                  </span>
                  <input
                    type="checkbox"
                    role="switch"
                    aria-label="AI processing"
                    checked={aiConsent === 'granted'}
                    onChange={(e) => setAiProcessing(e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 accent-brand-600"
                  />
                </label>
                <a
                  href={publicUrl('/privacy.html')}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block text-[11px] font-bold text-brand-600 dark:text-brand-400 underline-offset-2 hover:underline"
                >
                  Read the full Privacy & Data Security page →
                </a>
              </div>
            </section>

            {/* Data */}
            <section className="space-y-2">
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500">Data</h4>
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50 p-4">
                <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                  Your data belongs to you — export it, or delete it from
                  anywhere it lives, without asking anyone.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    onClick={exportData}
                    className="rounded-lg border border-slate-300 dark:border-slate-700 px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800"
                  >
                    ⬇ Export my data (JSON)
                  </button>
                  {syncStatus === 'syncing' && cloudDelete !== 'deleted' && (
                    <button
                      onClick={() => void deleteCloud()}
                      disabled={cloudDelete === 'working'}
                      className="rounded-lg border border-red-200 dark:border-red-500/20 px-4 py-2 text-xs font-semibold text-red-600 dark:text-red-400 transition-colors hover:bg-red-50 dark:hover:bg-red-950/20 disabled:opacity-60"
                    >
                      {cloudDelete === 'working' ? 'Deleting…' : 'Delete my cloud data'}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (
                        window.confirm(
                          'Erase everything on THIS DEVICE?\n\nYou will be signed out first, so your account in the cloud is NOT touched — sign in again to bring it back. To delete your account data, use "Delete my cloud data" instead.',
                        )
                      ) {
                        void import('../sync/firebaseSync').then((m) => m.eraseThisDevice());
                        closeSettings();
                      }
                    }}
                    className="rounded-lg border border-red-200 dark:border-red-500/20 px-4 py-2 text-xs font-semibold text-red-600 dark:text-red-400 transition-colors hover:bg-red-50 dark:hover:bg-red-950/20"
                  >
                    Erase this device
                  </button>
                </div>
                {cloudDelete === 'deleted' && (
                  <p className="mt-2 text-[11px] text-emerald-600 dark:text-emerald-400">
                    ✓ Cloud data deleted. Sync is off; your tasks remain on this device.
                  </p>
                )}
                {cloudDelete === 'error' && (
                  <p className="mt-2 text-[11px] text-red-600 dark:text-red-400">
                    Couldn't delete — check your connection and try again, or email us.
                  </p>
                )}
                {/* Apple 5.1.1(v): the account itself, in the app (13 Oct 2026). */}
                <DeleteAccountPanel />
              </div>
              {/* The Bin — accidental deletes are recoverable for 7 days. */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50 p-4">
                <div className="flex items-center justify-between">
                  <h5 className="text-xs font-bold text-slate-600 dark:text-slate-300">
                    🗑 Bin{visibleBin(bin).length > 0 ? ` (${visibleBin(bin).length})` : ''}
                  </h5>
                  {visibleBin(bin).length > 0 && (
                    <button
                      onClick={emptyBin}
                      className="text-[11px] font-semibold text-slate-400 transition-colors hover:text-red-500"
                    >
                      Empty bin
                    </button>
                  )}
                </div>
                {visibleBin(bin).length === 0 ? (
                  <p className="mt-2 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                    Deleted tasks land here for 7 days before they're removed
                    for good — restore one with everything intact (plan, tags,
                    notes, dates).
                  </p>
                ) : (
                  <ul className="mt-2 space-y-1.5">
                    {visibleBin(bin).map((e) => (
                      <li
                        key={e.task.id}
                        className="flex items-center justify-between gap-2 rounded-lg bg-white dark:bg-slate-900 px-3 py-2"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-xs font-medium text-slate-700 dark:text-slate-200">
                            {e.task.text}
                          </p>
                          <p className="text-[10px] text-slate-400">
                            {binDaysLeft(e, Date.now())} day
                            {binDaysLeft(e, Date.now()) === 1 ? '' : 's'} left
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1.5">
                          <button
                            onClick={() => {
                              setPurgeArmedId(null);
                              restoreFromBin(e.task.id);
                            }}
                            className="rounded-lg border border-brand-300 dark:border-brand-500/40 px-3 py-1.5 text-[11px] font-semibold text-brand-600 dark:text-brand-400 transition-colors hover:bg-brand-50 dark:hover:bg-brand-950/30"
                          >
                            Restore
                          </button>
                          {purgeArmedId === e.task.id ? (
                            <button
                              onClick={() => {
                                purgeFromBin(e.task.id);
                                setPurgeArmedId(null);
                              }}
                              className="rounded-lg border border-red-400 bg-red-500/10 px-2.5 py-1.5 text-[11px] font-bold text-red-600 dark:text-red-400"
                            >
                              Sure?
                            </button>
                          ) : (
                            <button
                              onClick={() => setPurgeArmedId(e.task.id)}
                              aria-label={`Delete "${e.task.text}" forever`}
                              title="Delete forever"
                              className="rounded-lg border border-slate-200 dark:border-slate-800 px-2.5 py-1.5 text-[11px] font-semibold text-slate-400 transition-colors hover:border-red-300 hover:text-red-500"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            {/* Help */}
            <section className="space-y-2">
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500">Help</h4>
              <a
                href={publicUrl('/guide.html')}
                target="_blank"
                rel="noreferrer"
                className="block rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50 p-4 text-sm font-semibold text-brand-600 dark:text-brand-400 transition-colors hover:bg-slate-100 dark:hover:bg-slate-900"
              >
                📖 Open the User Guide
                <span className="mt-0.5 block text-[11px] font-normal text-slate-500 dark:text-slate-400">
                  Zones, action plans, weekly rollover, sync, and troubleshooting.
                </span>
              </a>
            </section>

            <p className="pb-2 text-center text-[11px] text-slate-400 dark:text-slate-600">
              ZoneIn AI v{APP_VERSION} · {billingFooter()}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
